<section class="application-form-layout relative isolate flex min-w-0 flex-col overflow-hidden">
  <div class="application-form-header w-full shrink-0 bg-slate100 pb-6">
    <header
      class="relative z-[1] flex flex-wrap items-center justify-between gap-4 rounded-lg border border-slate200 bg-white px-5 py-3 shadow-sm"
      >
      <div class="flex items-center gap-3">
        @if (resolvedHeadingIcon) {
          <div
            class="flex h-10 w-10 items-center justify-center rounded-md text-indigo50 bg-indigo600"
            >
            <app-icon-reusable
              [icon]="resolvedHeadingIcon"
              [size]="20"
              [useCurrentColor]="true"
            ></app-icon-reusable>
          </div>
        }

        <div class="flex flex-col">
          <h1 class="text-[16px] font-semibold text-slate900">{{ heading }}</h1>
          @if (subheading) {
            <p class="text-xs text-slate500">{{ subheading }}</p>
          }
        </div>
      </div>

      @if (actionLabel) {
        <button
          type="button"
          class="inline-flex items-center gap-2 rounded-lg border border-slate200 bg-white px-3 py-2 text-sm font-semibold text-slate600 transition hover:border-slate300 hover:text-slate900"
          (click)="onActionClick()"
          >
          @if (resolvedActionIcon) {
            <app-icon-reusable
              [icon]="resolvedActionIcon"
              class="mt-1"
              [size]="16"
              [useCurrentColor]="true"
            ></app-icon-reusable>
          }
          <span>{{ actionLabel }}</span>
        </button>
      }
    </header>
  </div>
  <div class="application-form-body min-h-0 flex-1 overflow-y-auto">
    <div class="relative z-0 grid gap-6 pb-4 lg:grid-cols-[minmax(0,1fr)_360px] px-40">
      <section>
        <ng-content></ng-content>
      </section>

      <aside class="lg:sticky lg:top-0 lg:self-start">
        <div class="flex flex-col gap-5 rounded-lg border border-slate200 bg-white p-5 shadow-lg">
          <div class="border-b border-slate200 pb-3">
            <h2 class="text-fs32 font-semibold text-slate900">{{ evaluationTitle }}</h2>
          </div>

          <div class="flex flex-col gap-2.5">
            <p class="text-lg font-semibold text-slate900">{{ evaluationSubtitle }}</p>
            <p class="text-xs text-slate500 leading-4">
              <span class="font-semibold text-green600">Pass:</span>
              {{ passDescription }}
            </p>
            <p class="text-xs text-slate500 leading-4">
              <span class="font-semibold text-red600">Fail:</span>
              {{ failDescription }}
            </p>

            <div class="mt-2 flex flex-col gap-3">
              <label
                class="flex cursor-pointer items-center justify-between gap-3 rounded-lg border p-3 transition"
              [ngClass]="
                outcome === 'pass'
                  ? 'border-green400 bg-green50'
                  : 'border-slate300 bg-white hover:border-green200'
              "
                >
                <input
                  class="sr-only"
                  type="radio"
                  [name]="outcomeGroupName"
                  [checked]="outcome === 'pass'"
                  (change)="selectOutcome('pass')"
                  />
                <span class="flex flex-col">
                  <span class="text-sm font-semibold text-slate900">{{ passLabel }}</span>
                  <span class="text-sm text-slate700">{{ passStatusText }}</span>
                </span>
                <span
                  class="flex h-5 w-5 items-center justify-center rounded-full border-2 bg-white transition"
                  [ngClass]="outcome === 'pass' ? 'border-green600' : 'border-slate300'"
                  aria-hidden="true"
                  >
                  <span
                    class="h-2.5 w-2.5 rounded-full transition"
                    [ngClass]="outcome === 'pass' ? 'bg-green600' : 'bg-transparent'"
                  ></span>
                </span>
              </label>

              <label
                class="flex cursor-pointer items-center justify-between gap-3 rounded-lg border p-3 transition"
              [ngClass]="
                outcome === 'fail'
                  ? 'border-red400 bg-red50'
                  : 'border-slate300 bg-white hover:border-red200'
              "
                >
                <input
                  class="sr-only"
                  type="radio"
                  [name]="outcomeGroupName"
                  [checked]="outcome === 'fail'"
                  (change)="selectOutcome('fail')"
                  />
                <span class="flex flex-col">
                  <span class="text-sm font-semibold text-slate900">{{ failLabel }}</span>
                  <span class="text-sm text-slate700">{{ failStatusText }}</span>
                </span>
                <span
                  class="flex h-5 w-5 items-center justify-center rounded-full border-2 bg-white transition"
                  [ngClass]="outcome === 'fail' ? 'border-red500' : 'border-slate300'"
                  aria-hidden="true"
                  >
                  <span
                    class="h-2.5 w-2.5 rounded-full transition"
                    [ngClass]="outcome === 'fail' ? 'bg-red500' : 'bg-transparent'"
                  ></span>
                </span>
              </label>
            </div>

            @if (showOutcomeError) {
              <p class="text-xs font-medium text-red600">
                Please select an interview outcome before continuing.
              </p>
            }
          </div>

          <div class="flex flex-col gap-2 border border-slate300 rounded-md p-3">
            <p class="text-lg font-semibold text-slate900">{{ commentsLabel }}</p>
            <p class="text-fs10 text-slate500">{{ commentsHelper }}</p>
            <textarea
              class="min-h-[120px] rounded-lg border border-slate200 px-4 py-3 text-sm text-slate900 outline-none transition focus:border-indigo500 focus:ring-4 focus:ring-indigo200/40"
              [attr.maxLength]="maxCommentLength"
              [placeholder]="commentsPlaceholder"
              [value]="comments"
              (input)="onCommentsInput($event)"
            ></textarea>
            <p class="text-xs text-slate400 text-right">
              {{ commentCount }} / {{ maxCommentLength }}
            </p>
          </div>

          <div>
            <app-button-reusable
              variant="primary"
              size="lg"
              [fullWidth]="true"
              [hasIcon]="true"
              [icon]="submitEvaluationIcon"
              iconPosition="left"
              class="mt-1"
              [iconUseCurrentColor]="true"
              [disabled]="disableSubmit"
              (clicked)="onSubmitRequest()"
              >
              {{ submitLabel }}
            </app-button-reusable>
          </div>
        </div>
      </aside>
    </div>
  </div>
</section>

@if (evaluationConfirmationOpen) {
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/35 p-4"
    (click)="onConfirmationBackdropClick($event)"
    >
    <div
      class="w-full max-w-[440px] rounded-2xl bg-white p-6 shadow-[0_24px_80px_rgba(15,23,42,0.22)]"
      (click)="$event.stopPropagation()"
      >
      <div class="mx-auto flex h-[72px] w-[72px] items-center justify-center rounded-full">
        <div
          class="flex h-[60px] w-[60px] items-center justify-center rounded-full shadow-inner"
          [ngClass]="outcome === 'fail' ? 'bg-red100 text-red600' : 'bg-green100 text-green700'"
          >
          <div
            class="flex h-12 w-12 items-center justify-center rounded-full border-[6px]"
            [ngClass]="outcome === 'fail' ? 'border-red200 bg-red500' : 'border-green200 bg-green700'"
            >
            @if (outcome === 'fail') {
              <svg
                viewBox="0 0 20 20"
                class="h-5 w-5 text-white"
                fill="none"
                stroke="currentColor"
                stroke-width="2.25"
                stroke-linecap="round"
                >
                <path d="M6 6l8 8"></path>
                <path d="M14 6 6 14"></path>
              </svg>
            } @else {
              <svg
                viewBox="0 0 20 20"
                class="h-5 w-5 text-white"
                fill="none"
                stroke="currentColor"
                stroke-width="2.25"
                stroke-linecap="round"
                stroke-linejoin="round"
                >
                <path d="M4.5 10.5l3.5 3.5 7-8"></path>
              </svg>
            }
          </div>
        </div>
      </div>
      <div class="mt-5 text-center">
        <h3 class="text-[18px] font-semibold text-slate900">{{ confirmationTitle }}</h3>
        <p class="mt-3 text-sm leading-6 text-slate500">
          {{ confirmationDescription }}
        </p>
      </div>
      @if (outcome === 'pass') {
        <div
          class="mt-6 rounded-xl border border-green200 bg-green50/60 p-4"
          >
          <div class="flex items-center justify-between gap-4 border-b border-green200 py-3 first:pt-0">
            <span class="text-sm font-medium text-slate800">Interview Status</span>
            <span
              class="inline-flex items-center gap-1 rounded-full border border-green200 bg-white px-2.5 py-1 text-xs font-semibold text-green700"
              >
              <span class="h-1.5 w-1.5 rounded-full bg-green600"></span>
              {{ confirmationStatusLabel }}
            </span>
          </div>
          <div class="flex items-center justify-between gap-4 border-b border-green200 py-3">
            <span class="text-sm font-medium text-slate800">Interview Date</span>
            <span class="text-xs font-medium text-slate500">{{ formattedInterviewDate }}</span>
          </div>
          <div class="flex items-center justify-between gap-4 pt-3">
            <span class="text-sm font-medium text-slate800">Reviewed By</span>
            <span class="text-xs font-medium text-slate500">{{ reviewerDisplayName }}</span>
          </div>
        </div>
      } @else {
        <div class="mt-6">
          <label class="mb-2 block text-sm font-medium text-slate900">
            Failure Reason
            <span class="text-red600">*</span>
          </label>
          <textarea
            class="min-h-[92px] w-full rounded-lg border border-slate200 bg-slate50 px-4 py-3 text-sm text-slate900 outline-none transition focus:border-red400 focus:ring-4 focus:ring-red100/60"
            placeholder="Add failure reason..."
            [value]="failureReason"
            (input)="onFailureReasonInput($event)"
          ></textarea>
          @if (showFailureReasonError) {
            <p class="mt-2 text-xs font-medium text-red600">
              Please add a failure reason before confirming.
            </p>
          }
        </div>
      }
      <div class="mt-6">
        <app-button-reusable
          [variant]="outcome === 'fail' ? 'danger' : 'success'"
          size="lg"
          [fullWidth]="true"
          [loading]="disableSubmit"
          [disabled]="disableSubmit"
          (clicked)="onConfirmSubmit()"
          >
          Confirm
        </app-button-reusable>
      </div>
    </div>
  </div>
}
